import {FangkarteListItem} from '../models/FangkarteListItem';
import {ExportToCsv} from 'export-to-csv';
import TimestampFormat from '../utils/TimestampFormat';
import React, {useMemo, useState} from 'react';
import {Button, Dropdown} from 'react-bootstrap';
import {DownloadExcelCsvButton} from './DownloadExcelCsvButton';

const listGroupItemNormal = 'list-group-item cursor-pointer';
//const listGroupItemActive = "list-group-item cursor-pointer active";

export default function FangkarteList({
                                        listItems,
                                        title,
                                        onItemClicked,
                                        onItemAdd,
                                        onSwitchChanged,
                                        exportListEnabled = false,
                                        addNewElement = false,
                                        deleteElement = false,
                                        showSwitch = false,
                                        updateElement = false,
                                        onItemDelete,
                                        onItemUpdate,
                                        customDownloadLink,
                                        scrollable = false,
                                        contentPaddingBottom = '0.75rem',
                                        headerExtra,
                                        proSearchEnabled = false,
                                        searchPlaceholder = 'Suchen…',
                                        searchKeyBuilder,
                                        cardClassName,
                                        addButtonLabel = 'Neuer Eintrag',
                                        addButtonClassName,
                                        addButtonVariant = 'primary',
                                        actionsInMenu = false,
                                        downloadLabel = 'Download',
                                        islDownloadLabel = 'iSL Download',
                                        islDownloadLink,
                                        selectedItemId,
                                        getItemId

                                      }: {
  listItems: FangkarteListItem[];
  title: string | undefined;
  onItemClicked?: (any: any) => void;
  onItemDelete?: (any: any) => void;
  onItemAdd?: (any: any) => void;
  onItemUpdate?: (any: any) => void;
  onSwitchChanged?: (any: any) => void;
  exportListEnabled?: boolean;
  addNewElement?: boolean;
  deleteElement?: boolean;
  updateElement?: boolean;
  showSwitch?: boolean;
  customDownloadLink?: string | undefined;
  scrollable?: boolean;
  contentPaddingBottom?: string;
  headerExtra?: React.ReactNode;
  proSearchEnabled?: boolean;
  searchPlaceholder?: string;
  searchKeyBuilder?: (item: FangkarteListItem) => string;
  cardClassName?: string;
  addButtonLabel?: string;
  addButtonClassName?: string;
  addButtonVariant?: string;
  actionsInMenu?: boolean;
  downloadLabel?: string;
  islDownloadLabel?: string;
  islDownloadLink?: string;
  selectedItemId?: string;
  getItemId?: (item: FangkarteListItem) => string | undefined;

}) {
  const [query, setQuery] = useState('');

  const buildSearchKey = useMemo(() => {
    return (
      searchKeyBuilder ??
      ((item: FangkarteListItem) =>
        `${item.title ?? ''} ${item.subTitle ?? ''}`.toLowerCase())
    );
  }, [searchKeyBuilder]);

  const visibleItems = useMemo(() => {
    if (!proSearchEnabled) return listItems;

    const q = query.trim().toLowerCase();
    if (!q) return listItems;

    return listItems.filter((item) => buildSearchKey(item).includes(q));
  }, [listItems, proSearchEnabled, query, buildSearchKey]);


  const createList = () => {
    return (
      <div className="list-group list-group-flush list-group-hoverable">
        {visibleItems.map((item) => (
          <React.Fragment key={`${item.title}-${item.subTitle}`}>
            {createListElement(item)}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const createListElement = (listItem: FangkarteListItem) => {
    const isActive = selectedItemId && getItemId
      ? getItemId(listItem) === selectedItemId
      : false;
    const activeStyles = isActive
      ? {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        borderLeft: '3px solid rgba(0, 0, 0, 0.18)'
      }
      : undefined;
    return (
      <div
        className={`${listGroupItemNormal}`}
        style={activeStyles}
        onClick={() =>
          // TODO (philo.koenneker 03.01.2024) erweitern um "id" und "selected" felder im ListItem
          onItemClicked ? onItemClicked(listItem.originalElement) : ''
        }
      >
        <div className="row divider align-items-center">
          <div className="col-auto d-flex align-items-center">
            {deleteElement && !updateElement ? showDeleteIcon(listItem) : ''}
            {!deleteElement && updateElement ? showUpdateIcon(listItem) : ''}
            {deleteElement && updateElement
              ? showUpdateDeleteDropdown(listItem)
              : ''}
          </div>
          <div className="col-auto">
            <div className="col text-truncate">
              <span className={isActive ? 'fw-semibold' : ''}>
                {listItem.title}
              </span>
              <div className={`d-block text-truncate mt-n1 ${isActive ? 'text-muted' : 'text-muted'}`}>
                {listItem.subTitle}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const showUpdateDeleteDropdown = (listItem: FangkarteListItem) => {
    return (
      <Dropdown>
        <Dropdown.Toggle variant="link" id="dropdown-basic">
          ...
        </Dropdown.Toggle>

        <Dropdown.Menu className="dropdown-menu-end">
          <Dropdown.Item
            onClick={() =>
              onItemUpdate ? onItemUpdate(listItem.originalElement) : ''
            }
          >
            Bearbeiten
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() =>
              onItemDelete ? onItemDelete(listItem.originalElement) : ''
            }
          >
            Löschen
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const showDeleteIcon = (listItem: FangkarteListItem) => {
    return (
      <Button
        onClick={() =>
          onItemDelete ? onItemDelete(listItem.originalElement) : ''
        }
        variant="danger"
        className="d-flex align-items-center justify-content-center p-0"
        style={{width: 40, height: 40}}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="icon icon-tabler icon-tabler-trash"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{display: "block", margin: "0 auto"}}
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M4 7l16 0"></path>
          <path d="M10 11l0 6"></path>
          <path d="M14 11l0 6"></path>
          <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
          <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
        </svg>
      </Button>
    );
  };

  const showUpdateIcon = (listItem: FangkarteListItem) => {
    return (
      <Button
        onClick={() =>
          onItemUpdate ? onItemUpdate(listItem.originalElement) : ''
        }
        variant="danger"
        className="d-flex align-items-center justify-content-center p-0"
        style={{width: 40, height: 40}}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="icon icon-tabler icon-tabler-trash"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{display: "block", margin: "0 auto"}}
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M4 7l16 0"></path>
          <path d="M10 11l0 6"></path>
          <path d="M14 11l0 6"></path>
          <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
          <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
        </svg>
      </Button>
    );
  };

  const downloadCSV = () => {
    if (exportListEnabled) {
      const data = listItems.map((elem) => {
        return elem.originalElement;
      });

      data.forEach((elem) => {
        const keys = Object.keys(elem);
        keys.forEach((key) => {
          if (!isNaN(elem[key]) && elem[key] > 1000000000) {
            try {
              elem[key] = TimestampFormat.toDateOnlyReadableFormat(elem[key]);
            } catch (e) {
              console.error(e);
            }
          }
        });
      });

      const options = {
        fieldSeparator: ';',
        quoteStrings: '"',
        decimalSeparator: '',
        showLabels: false,
        showTitle: false,
        title: 'Export',
        useTextFile: false,
        useBom: false,
        useKeysAsHeaders: false
      };

      const csvExporter = new ExportToCsv(options);

      csvExporter.generateCsv(data);

      console.log(JSON.stringify(data));
    }
  };

  const createAddItemAction = () => {
    return (
      <div className="card-actions">
        <Button
          variant={addButtonVariant}
          className={addButtonClassName}
          onClick={onItemAdd}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-plus"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M12 5l0 14"></path>
            <path d="M5 12l14 0"></path>
          </svg>
          {addButtonLabel}
        </Button>
      </div>
    );
  };

  const createDownloadAction = () => {
    return (
      <div className="card-actions">
        <button className="btn" onClick={downloadCSV}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-download"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"></path>
            <path d="M7 11l5 5l5 -5"></path>
            <path d="M12 4l0 12"></path>
          </svg>
          Download
        </button>
      </div>
    );
  };

  const createActionsMenu = () => {
    return (
      <Dropdown align="end">
        <Dropdown.Toggle
          variant="link"
          className="btn btn-link p-0 text-decoration-none"
          id="actions-menu"
          aria-label="Aktionen"
        >
          <span className="fw-semibold">⋮</span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="dropdown-menu-end"
          style={{right: 0, left: 'auto', minWidth: 200}}
        >
          {customDownloadLink ? (
            <Dropdown.Item>
              <DownloadExcelCsvButton
                downloadUrl={customDownloadLink}
                label={downloadLabel}
                className="text-decoration-none text-dark"
              />
            </Dropdown.Item>
          ) : null}
          {exportListEnabled ? (
            <Dropdown.Item onClick={downloadCSV} className="text-dark">
              Download
            </Dropdown.Item>
          ) : null}
          {addNewElement ? (
            <Dropdown.Item onClick={onItemAdd} className="text-dark">
              <span className="d-inline-flex align-items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M12 5l0 14"></path>
                  <path d="M5 12l14 0"></path>
                </svg>
                {addButtonLabel}
              </span>
            </Dropdown.Item>
          ) : null}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const createDownloadActionForCustomDownload = () => {
    return (
      <div className="card-actions">
        <DownloadExcelCsvButton
          downloadUrl={`${customDownloadLink}`} />
      </div>
    );
  };

  const createSwitchElement = () => {
    return (
      <label className="form-check form-switch">
        <input
          type="checkbox"
          className="form-check-input"
          onChange={onSwitchChanged}
        />
        <span className="form-check-label">Zurückliegende</span>
      </label>
    );
  };

  const headerActionsClass = `d-flex align-items-center gap-2${
    actionsInMenu ? ' flex-grow-1 justify-content-end' : ''
  }`;

  return (
    <div
      className={`card shadow-sm ${cardClassName ?? ''} ${
        scrollable ? 'h-100 d-flex flex-column' : ''
      }`}
         style={scrollable ? {minHeight: 0} : undefined}>
      <div
        className="card-header"
        style={
          scrollable
            ? {
              position: 'sticky',
              top: 0,
              zIndex: 10,
              background: '#fff',
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
              flex: '0 0 auto'
            }
            : undefined
        }
      >
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div>
            <h3 className="card-title">{title}</h3>
            {showSwitch ? createSwitchElement() : ''}
          </div>

          <div className={headerActionsClass}>
            {proSearchEnabled ? (
              <input
                className="form-control form-control-sm"
                style={{minWidth: 220}}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label="Liste durchsuchen"
              />
            ) : null}

            {/* von außen rein (z.B. Suche) */}
            {headerExtra ? <div style={{minWidth: 220}}>{headerExtra}</div> : null}

            {actionsInMenu ? (
              createActionsMenu()
            ) : (
              <>
                {exportListEnabled ? createDownloadAction() : ''}
                {customDownloadLink ? createDownloadActionForCustomDownload() : ''}
                {addNewElement ? createAddItemAction() : ''}
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className="card-body"
        style={
          scrollable
            ? {
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              paddingBottom: contentPaddingBottom
            }
            : undefined
        }
      >
        {createList()}
      </div>
    </div>
  );
}
